/**
 * Shared deck-upload failure classification and copy for every surface that
 * takes a pitch deck (onboarding, Fundraising, task-evidence, Labs pitch
 * eval). Browser+server safe: no "use client", no server-only imports, so
 * both the client toast path and a server route's error body can import it.
 *
 * One `DeckUploadFailureKind` replaces four surfaces independently guessing
 * at a cause from a raw error — "Failed to process file" told a founder
 * nothing about whether to retry, export a PDF, or give up. Each kind maps
 * to one specific, actionable message (LEV-2923); the kinds and wording are
 * Jay-approved verbatim and MUST NOT be edited without going back to him.
 */
import {
  DOCX_NO_TEXT_ERROR,
  PDF_NO_TEXT_ERROR,
  PPTX_NO_SLIDES_ERROR,
  PPTX_NO_TEXT_ERROR,
} from "@/constants/deckParse";

export const DECK_UPLOAD_FAILURE_KINDS = [
  "unsupported_format",
  "too_large",
  "upload_failed",
  "timed_out",
  "no_text",
  "password_protected",
  "corrupt",
  "server_error",
  "rate_limited",
  "unknown",
] as const;

export type DeckUploadFailureKind = (typeof DECK_UPLOAD_FAILURE_KINDS)[number];

const DECK_UPLOAD_FAILURE_KIND_SET: ReadonlySet<string> = new Set(
  DECK_UPLOAD_FAILURE_KINDS,
);

export function isDeckUploadFailureKind(value: unknown): value is DeckUploadFailureKind {
  return typeof value === "string" && DECK_UPLOAD_FAILURE_KIND_SET.has(value);
}

/** Where in the flow the failure happened — drives which copy variant (if any) offers the paste fallback. */
export type DeckUploadPhase = "pre_upload" | "upload" | "read";

/** Which flow hit the failure, for the analytics event's `surface` property. */
export type DeckUploadSurface = "onboarding" | "fundraising" | "task_proof" | "labs_pitch";

export type DeckUploadFormat = "pdf" | "pptx" | "docx" | "key" | "ppt" | "gslides" | "other";

/**
 * Format from the filename extension, case-insensitive. Distinct from
 * `deckFormatFromFilename` in `DeckUploadService.ts` (which only knows the
 * two formats the upload transfer itself accepts): this one also names the
 * formats people mistakenly upload (.key, .ppt, .gslides) so the
 * unsupported_format message can name the right export path.
 */
export function deckUploadFormatOf(fileName: string): DeckUploadFormat {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".pptx")) return "pptx";
  if (lower.endsWith(".docx")) return "docx";
  if (lower.endsWith(".key")) return "key";
  if (lower.endsWith(".ppt")) return "ppt";
  if (lower.endsWith(".gslides")) return "gslides";
  return "other";
}

export type DeckUploadSizeBucket = "<5mb" | "5-15mb" | "15-30mb" | "30-50mb" | "50mb+";

/** Bucket a byte count for analytics — never the raw size (analytics conventions: no file-identifying detail). */
export function deckUploadSizeBucket(bytes: number): DeckUploadSizeBucket {
  const mb = bytes / (1024 * 1024);
  if (mb < 5) return "<5mb";
  if (mb < 15) return "5-15mb";
  if (mb < 30) return "15-30mb";
  if (mb < 50) return "30-50mb";
  return "50mb+";
}

/** Render a byte count in MB, dropping a trailing `.0`. */
export function formatFileSize(bytes: number): string {
  const mb = Math.round((bytes / (1024 * 1024)) * 10) / 10;
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

/** The file is wrong (format or size) — nothing was sent. DeckUploadService throws this class, so `classifyDeckUploadError` can match it with instanceof. */
export class DeckUploadValidationError extends Error {
  constructor(
    readonly reason: "format" | "size",
    readonly maxBytes: number,
  ) {
    super(
      reason === "format"
        ? "Only PDF and PPTX decks are supported."
        : `That deck exceeds the ${Math.round(maxBytes / (1024 * 1024))}MB limit.`,
    );
    this.name = "DeckUploadValidationError";
  }
}

/** The transfer failed (or was aborted) — the file itself was acceptable. */
export class DeckUploadTransferError extends Error {
  constructor(
    readonly aborted: boolean,
    readonly cause?: unknown,
  ) {
    super(aborted ? "Deck upload aborted." : "Deck upload failed.");
    this.name = "DeckUploadTransferError";
  }
}

function readErrorData(err: unknown): { code?: unknown; httpStatus?: unknown } {
  if (typeof err !== "object" || err === null || !("data" in err)) return {};
  const data = (err as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return {};
  return data as { code?: unknown; httpStatus?: unknown };
}

function readErrorMessage(err: unknown): string {
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
}

// Password detection reads the message text, not an error class: a job's
// thrown error does not survive the Inngest step boundary — onFailure only
// ever sees a plain message string, never the original pdf-parse exception.
const PASSWORD_PROTECTED_TEXT = /no password given|incorrect password/i;
const CORRUPT_TEXT =
  /invalid pdf structure|invalid .* central directory|not a valid (?:docx|pptx) archive|not a valid pdf|unsupported zip structure|suspicious compression ratio/i;
// The platform's own body-size rejections ("Body exceeded 4.5mb limit",
// "request entity too large") can arrive without a typed 413 status.
const TOO_LARGE_TEXT = /exceeds the .* limit|body exceeded .* limit|request entity too large/i;
const TIMED_OUT_TEXT = /timed out|timeout/i;

/**
 * Classify a deck-READ failure (parse-time: corrupt file, password, no
 * extractable text, read timeout) from its thrown error or message string.
 * Order matters: the shared no-text constants are checked first since they
 * are exact strings, before the looser message regexes below them.
 */
export function classifyDeckReadError(error: unknown): DeckUploadFailureKind {
  const message = readErrorMessage(error);
  if (!message) return "unknown";

  if (
    message.includes(PDF_NO_TEXT_ERROR) ||
    message.includes(DOCX_NO_TEXT_ERROR) ||
    message.includes(PPTX_NO_TEXT_ERROR) ||
    message.includes(PPTX_NO_SLIDES_ERROR)
  ) {
    return "no_text";
  }
  if (PASSWORD_PROTECTED_TEXT.test(message)) return "password_protected";
  if (CORRUPT_TEXT.test(message)) return "corrupt";
  if (TOO_LARGE_TEXT.test(message)) return "too_large";
  if (TIMED_OUT_TEXT.test(message)) return "timed_out";
  return "unknown";
}

/**
 * Classify an upload-TRANSFER failure (client blob upload, or an HTTP/tRPC
 * error from a parse route) into the same kind space as a read failure, so
 * every caller has one function regardless of which stage failed.
 *
 * Status checks run before the generic 5xx bucket so a gateway timeout is
 * not reported as a server crash. The read-message check runs before that
 * bucket too: a parse route with no typed code for "password protected"
 * surfaces it as a plain 500, and the specific cause must win or every real
 * failure reads as "something broke" (ported from the Labs
 * `classifyUploadFailure`, src/app/[projectId]/labs/vc-pitch-evaluation/lib/uploadErrors.ts).
 */
export function classifyDeckUploadError(err: unknown): DeckUploadFailureKind {
  if (err instanceof DeckUploadValidationError) {
    return err.reason === "format" ? "unsupported_format" : "too_large";
  }
  if (err instanceof DeckUploadTransferError) {
    // The wrapped cause can name a specific failure (a 413 from the presign
    // route); anything it cannot name is still an upload that never finished.
    const causeKind = err.cause === undefined ? "unknown" : classifyDeckUploadError(err.cause);
    return causeKind === "unknown" ? "upload_failed" : causeKind;
  }

  const { code, httpStatus } = readErrorData(err);
  const status = typeof httpStatus === "number" ? httpStatus : null;

  if (status === 413 || code === "PAYLOAD_TOO_LARGE") return "too_large";
  if (status === 408 || status === 504 || code === "TIMEOUT") return "timed_out";
  if (status === 429 || code === "TOO_MANY_REQUESTS") return "rate_limited";
  // Labs pitch extraction answers "not enough readable content" with 422.
  if (status === 422 || code === "UNPROCESSABLE_CONTENT") return "no_text";

  const readKind = classifyDeckReadError(err);
  if (readKind !== "unknown") return readKind;

  if (status !== null && status >= 500) return "server_error";

  return "unknown";
}

export interface DeckUploadFailureMessageContext {
  /** The surface's byte cap, when known. too_large reads the real number instead of a hard-coded one, so one helper serves both the 30MB onboarding cap and the 50MB Fundraising/task-evidence cap. */
  limitBytes?: number;
  /** The file's own size, when known — too_large prefers naming both numbers over just the limit. */
  sizeBytes?: number;
  /** Human-readable accepted-format list for THIS surface (e.g. "a PDF or PPTX"). Used only by unsupported_format's generic fallback branch. */
  accepted?: string;
  /** The rejected file's format — picks the per-app export instructions for unsupported_format, and the "{formatLabel}" noun for corrupt. */
  format?: DeckUploadFormat;
  /** Onboarding only: a text-paste fallback exists alongside file upload, so no_text/rate_limited/unknown there also offer it. */
  canPaste?: boolean;
}

const FORMAT_LABEL: Record<DeckUploadFormat, string> = {
  pdf: "PDF",
  pptx: "PowerPoint file",
  docx: "Word file",
  key: "Keynote file",
  ppt: "PowerPoint file",
  gslides: "Google Slides file",
  other: "file",
};

/** The three kinds the onboarding paste fallback applies to. A kind not in this set never gets the sentence, even with canPaste set. */
const KINDS_WITH_PASTE_FALLBACK: ReadonlySet<DeckUploadFailureKind> = new Set([
  "no_text",
  "rate_limited",
  "unknown",
]);

const PASTE_SENTENCE = " Or paste the key points into the chat.";

function baseDeckUploadFailureMessage(
  kind: DeckUploadFailureKind,
  ctx: DeckUploadFailureMessageContext,
): string {
  switch (kind) {
    case "unsupported_format":
      if (ctx.format === "key") {
        return "Keynote files can't be read here. In Keynote, choose File > Export To > PDF, then upload the PDF.";
      }
      if (ctx.format === "ppt") {
        return "Older .ppt files can't be read here. In PowerPoint, choose File > Save As and pick PDF, then upload the PDF.";
      }
      if (ctx.format === "gslides") {
        return "Google Slides shortcut files can't be read here. In Google Slides, choose File > Download > PDF Document, then upload the PDF.";
      }
      return `This file type can't be read here. Upload a ${ctx.accepted ?? "supported file"}. Most slide apps can save a copy as PDF under File > Export or File > Download.`;
    case "too_large":
      if (ctx.sizeBytes != null && ctx.limitBytes != null) {
        return `This file is ${formatFileSize(ctx.sizeBytes)} and the limit here is ${formatFileSize(ctx.limitBytes)}. Export a smaller PDF under ${formatFileSize(ctx.limitBytes)} with compressed images, then upload again.`;
      }
      if (ctx.limitBytes != null) {
        return `This file is over the ${formatFileSize(ctx.limitBytes)} limit. Export a smaller PDF under ${formatFileSize(ctx.limitBytes)} with compressed images, then upload again.`;
      }
      return "This file is too large to process. Export a smaller PDF with compressed images and fewer slides, then upload again.";
    case "upload_failed":
      return "This file didn't finish uploading. Check your connection, then upload it again.";
    case "timed_out":
      return "Reading this file took too long. Export a smaller PDF with compressed images, then upload again.";
    case "no_text":
      return "No readable text was found in this file. If the slides are scans or images, export a PDF from the original file (not a scan) and upload that.";
    case "password_protected":
      return "This file is password protected, so it can't be opened. Save a copy without the password, or export it as PDF, then upload that.";
    case "corrupt":
      return `This file couldn't be opened: its contents aren't a valid ${FORMAT_LABEL[ctx.format ?? "other"]}. Open it in the app that made it, save a fresh copy as PDF, then upload that.`;
    case "server_error":
      return "Lev hit an error while handling this file. Upload it again in a minute. If it keeps failing, export it as PDF and try that.";
    case "rate_limited":
      return "You've reached the upload limit for now. Try again within the hour.";
    case "unknown":
      return "This file couldn't be read. Export it as PDF, then upload again.";
  }
}

/**
 * User-facing wording for a deck-upload failure kind. Messages are Jay-
 * approved verbatim (LEV-2923 plan) — change the wording only with his
 * sign-off, not as part of an unrelated edit.
 */
export function deckUploadFailureMessage(
  kind: DeckUploadFailureKind,
  ctx: DeckUploadFailureMessageContext = {},
): string {
  const message = baseDeckUploadFailureMessage(kind, ctx);
  if (ctx.canPaste && KINDS_WITH_PASTE_FALLBACK.has(kind)) {
    return `${message}${PASTE_SENTENCE}`;
  }
  return message;
}
